import { useEffect } from 'react';
import { BeforeSubmitModalProps } from 'domain/sales/opportunity/presale/request/shared/modal/BeforeSubmitModal';
import {
  FormProvider,
  useFormContext,
  useForm,
  getSubmissionErrorsFromResponse,
  isEmptyFormErrors,
} from 'framework/form';
import { useShowContext } from 'framework/show';
import PresaleOutputInputForm from 'domain/sales/opportunity/shared/OpportunityPipeline/button/MarkStateAsCompletedButton/PresaleOutputInputForm';
import { presaleOutputValidation, usePresaleOutputService } from 'domain/shared/presale';
import { useOpportunityId } from 'domain/shared/opportunity';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { Box } from '@mui/material';

const BeforeSubmitContractRequestModal = ({ modalOpen, setModalOpen }: BeforeSubmitModalProps) => {
  const id = useOpportunityId();
  const { submitForm = () => {} } = useFormContext();
  const { getLastPresaleOutput, updateOutput } = usePresaleOutputService();
  const { data: showData } = useShowContext();

  const contextValue = useForm({
    submitFormFunc: () => Promise.resolve({}),
    getValidationSchemaFunc: () => Promise.resolve(presaleOutputValidation),
  });

  useEffect(() => {
    (async () => {
      const lastOutput = await getLastPresaleOutput(id);
      const isContractRenewal = showData.getValueBySource('isContractRenewal');
      const initialOutputData = lastOutput ? { ...lastOutput, isContractRenewal } : lastOutput;
      contextValue.setFormData(initialOutputData);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, id]);

  const handleComplete = async () => {
    if (contextValue.data) {
      contextValue.setFormErrors({});
      const formData = contextValue.data;
      const formErrors = {
        ...contextValue.validateForm(),
        isValidExpectedProjectStart: !formData.isValidExpectedProjectStart ? ['Please, confirm!'] : [],
        isValidProjectType: !formData.isValidProjectType ? ['Please, confirm!'] : [],
        isValidRequiredResources: !formData.isValidRequiredResources ? ['Please, confirm!'] : [],
      };
      contextValue.setFormErrors(formErrors);
      if (!isEmptyFormErrors(formErrors)) {
        return;
      }
      try {
        const outputData = contextValue.data;
        await updateOutput(id, outputData.id, outputData);
      } catch (e) {
        const errors = await getSubmissionErrorsFromResponse(e as Response);
        contextValue.setFormErrors(errors);
        return;
      }
    }
    submitForm();
    setModalOpen(false);
  };

  const submit = (
    <Button data-testid="btn-success" key="modal-submit-button" onClick={handleComplete}>
      Submit
    </Button>
  );

  return (
    <>
      <Modal
        modalSize="lg"
        isOpen={modalOpen}
        title="Please ensure that the Team Structure and Expected Project Start Date are up to date."
        hasCancelButton={false}
        buttons={[submit]}
        onToggle={() => {
          setModalOpen(false);
        }}
      >
        <span>
          Check the Team Structure, Project Type and Expected Project Start Date. If the data is not up to date, please
          update it using the form below and click the Submit button.
        </span>
        <Box mt={2.5}>
          <FormProvider value={contextValue}>
            <PresaleOutputInputForm />
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default BeforeSubmitContractRequestModal;
